import React, { useEffect, useState } from "react";
import { useQuery, useMutation, NetworkStatus } from "@apollo/client";
import CancelModal from "../sales/components/cancel-modal";
import Header from "../../../../components/common/header";
import { useNavigate } from "react-router-dom";
import { GET_SELLER_STORE } from "../../api/seller-store";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import clsx from "clsx";
import LoadingSpinnerFull from "../../../../components/common/loading-spinner/loading-spinner-full";
import ResponsiveCDNImage from "../../../../components/common/image/responsive-cdn-image";
import { HiExclamationTriangle } from "react-icons/hi2";
import { FiPackage } from "react-icons/fi";
import { notify, notifyError } from "../../../../utils/toast";
import { CREATE_REPORT } from "../../api/pack-order-queries";
import { TOGGLE_PICKUP_COMPLETED } from "../../../../api/order-mutations";
import {
  CANCEL_PAYMENT,
  DELETE_PRODUCT,
  GET_PRODUCTS,
  GET_PRODUCT_SALES_DAILY_SUMMARIES,
} from "../../api/live-order-queries";
import type {
  GetProductSalesDailySummariesQuery,
  GetProductsQuery,
  GetProductsQueryVariables,
} from "../../../../__generated__/graphql";
import { ProductsBySellerDateScope } from "../../../../__generated__/graphql";

type Store = {
  id: number;
  img?: string;
  title: string;
  address: string;
  contactNumber: string;
  closingHours: string;
  lat: number;
  lng: number;
};

type PreviousSaleProduct = NonNullable<
  GetProductsQuery["productsBySeller"]
>[number];

const LiveOrderPreviousSales: React.FC = () => {
  const navigate = useNavigate();
  const PAGE_SIZE = 30;
  const productsQueryVars = {
    take: PAGE_SIZE,
    cursor: null,
    dateScope: ProductsBySellerDateScope.Previous,
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cancelingProduct, setCancelingProduct] =
    useState<PreviousSaleProduct | null>(null);
  const [reason, setReason] = useState("");
  const [loadingProductId, setLoadingProductId] = useState<number | null>(null);
  const { loading, error, data, fetchMore, networkStatus } = useQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS, {
    fetchPolicy: "network-only",
    variables: productsQueryVars,
    notifyOnNetworkStatusChange: true,
  });
  const {
    data: dailySummariesData,
    loading: dailySummariesLoading,
    error: dailySummariesError,
  } =
    useQuery<GetProductSalesDailySummariesQuery>(
      GET_PRODUCT_SALES_DAILY_SUMMARIES,
      {
        fetchPolicy: "network-only",
      }
    );

  const isFetchingMore = networkStatus === NetworkStatus.fetchMore;
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (loading) return;
    const firstCount = data?.productsBySeller?.length ?? 0;
    // fetchMore로 누적되면 length가 PAGE_SIZE를 초과하므로, "1페이지(초기/리페치)" 구간에서만 재계산
    if (firstCount <= PAGE_SIZE) {
      setHasMore(firstCount === PAGE_SIZE);
    }
  }, [loading, data, PAGE_SIZE]);

  const handleLoadMore = () => {
    if (loading || isFetchingMore || !hasMore) return;
    const current = data?.productsBySeller ?? [];
    if (current.length === 0) return;

    const lastId = current[current.length - 1]?.id;
    if (!lastId) return;

    let lastPageCount = 0;
    fetchMore({
      variables: {
        take: PAGE_SIZE,
        cursor: lastId,
        dateScope: ProductsBySellerDateScope.Previous,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        const prevList = prev?.productsBySeller ?? [];
        const nextList = fetchMoreResult?.productsBySeller ?? [];
        lastPageCount = nextList.length;

        const merged = new Map<number, PreviousSaleProduct>();
        [...prevList, ...nextList].forEach((product) =>
          merged.set(product.id, product)
        );

        // cursor(lastId)가 제대로 전진하도록 정렬 고정 (createdAt desc, id desc)
        const sorted = Array.from(merged.values()).sort((a, b) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          if (aTime !== bTime) return bTime - aTime;
          return (b.id ?? 0) - (a.id ?? 0);
        });

        return {
          ...prev,
          productsBySeller: sorted,
        };
      },
    })
      .then((res) => {
        // res.data는 merge된 전체 리스트일 수 있어서, "이번 페이지" 길이로 판단
        if (lastPageCount < PAGE_SIZE) setHasMore(false);
      })
      .catch(() => {
        // 무한 스크롤 추가 로딩 실패는 조용히 무시 (상단 에러 UI는 초기 로딩에서 처리)
      });
  };

  const [deleteProduct] = useMutation(DELETE_PRODUCT, {
    refetchQueries: [
      { query: GET_PRODUCTS, variables: productsQueryVars },
      { query: GET_PRODUCT_SALES_DAILY_SUMMARIES },
    ],
  });
  const [cancelPayment] = useMutation(CANCEL_PAYMENT, {
    refetchQueries: [
      { query: GET_PRODUCTS, variables: productsQueryVars },
      { query: GET_PRODUCT_SALES_DAILY_SUMMARIES },
    ],
  });
  const [completePickup] = useMutation(TOGGLE_PICKUP_COMPLETED, {
    refetchQueries: [{ query: GET_PRODUCTS, variables: productsQueryVars }],
  });

  const [store, setStore] = useState<Store | null>(null);
  useQuery(GET_SELLER_STORE, {
    onCompleted: (data) => {
      setStore(data.getSellerStore);
    },
  });

  const [isReportModalOpen, setReportModalOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [selectedReportOrderId, setSelectedReportOrderId] = useState<
    number | null
  >(null);
  const [createReport, { loading: reportLoading }] = useMutation(CREATE_REPORT);

  const handleEdit = (productId: number) => {
    const target = data?.productsBySeller?.find(
      (product) => product.id === productId
    );
    if (!target) return;

    if ((target.menus?.length ?? 0) >= 1) {
      navigate(`/boss/live-order/sales/product-update/${productId}`); // 메뉴에서 선택한 마감팩 업데이트
    } else {
      navigate(`/boss/live-order/sales/manual-update/${productId}`); // 직접 만든 마감팩 업데이트
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("정말로 삭제하시겠습니까?")) {
      setLoadingProductId(id);
      await deleteProduct({
        variables: { id },
        onCompleted: () => {
          notify("삭제되었습니다.");
        },
        onError: (error) => {
          notifyError(error.message);
        },
      });
      setLoadingProductId(null);
    }
  };

  const handleCancel = (product: PreviousSaleProduct) => {
    setCancelingProduct(product);
    setIsModalOpen(true);
  };

  const onConfirm = () => {
    if (!cancelingProduct?.order?.id) return;

    cancelPayment({
      variables: {
        id: cancelingProduct.order.id,
        reason,
      },
    });
  };

  const handleReason = (reason: string) => {
    setReason(reason);
  };

  const formatDateToKST = (dateKey: string) => {
    const date = new Date(`${dateKey}T00:00:00+09:00`);
    const options: Intl.DateTimeFormatOptions = {
      month: "numeric", // 월을 숫자로
      day: "numeric", // 일을 숫자로
      weekday: "short", // 요일을 짧은 형태로 (월, 화, 수 등)
      timeZone: "Asia/Seoul", // KST (UTC+9)
    };
    return new Intl.DateTimeFormat("ko-KR", options).format(date);
  };

  const getKstDateKey = (dateValue: string) => {
    const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
    return new Date(new Date(dateValue).getTime() + KST_OFFSET_MS)
      .toISOString()
      .slice(0, 10);
  };

  const handleReportOrder = (orderId: number, reason: string) => {
    createReport({
      variables: { orderId, reason },
      onCompleted: () => {
        notify("신속하게 확인 후에 연락드리겠습니다.");
      },
      onError: (error) => {
        notify(error.message);
      },
    });
  };

  const handleCompletePickup = (orderId: number) => {
    completePickup({
      variables: { id: orderId, triggeredBy: "seller" },
    });
  };

  // fetchMore 중에도 loading=true가 될 수 있어서, "초기 로딩"일 때만 풀 스피너 노출
  if ((loading && !data) || (dailySummariesLoading && !dailySummariesData)) {
    return <LoadingSpinnerFull />;
  }
  if ((error && !data) || (dailySummariesError && !dailySummariesData)) {
    return <p>오류: {error?.message ?? dailySummariesError?.message}</p>;
  }

  if (!store)
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <p className="text-lg mb-6 text-gray-700">
          매장이 아직 등록되지 않았습니다.
        </p>
        <button
          onClick={() => navigate("/boss/my/store-management")}
          className="px-6 py-3 bg-blue-500 text-white font-semibold rounded-lg shadow-md hover:bg-blue-600 transition duration-300"
        >
          매장 추가하러 가기
        </button>
      </div>
    );

  const isToday = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const previousOrders = (data?.productsBySeller ?? []).filter(
    (product) => !isToday(String(product.createdAt))
  );

  const groupedProducts = previousOrders.reduce<
    Record<string, PreviousSaleProduct[]>
  >((groups, product) => {
    const date = getKstDateKey(String(product.createdAt));
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(product);
    return groups;
  }, {});
  const registeredPackCountByDate = new Map(
    dailySummariesData?.productSalesDailySummariesBySeller.map((summary) => [
      summary.date,
      summary.registeredPackCount,
    ]) ?? []
  );

  return (
    <div className="min-h-screen bg-white">
      <Header
        title="이전 주문"
        showBackButton
        onBackClick={() =>
          navigate("/boss/live-order/sales", { replace: true })
        }
      />
      <div className="pt-4">
        {(data?.productsBySeller?.length ?? 0) > 0 ? (
          Object.keys(groupedProducts).map((date) => (
            <div key={date} className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 rounded-md pl-4">
                {formatDateToKST(date)}
              </h3>
              <div className="bg-black h-[2px] w-full rounded-xl" />
              <div className="mx-3 mt-4 flex items-center justify-between rounded-xl bg-gray-50 px-4 py-4">
                <div className="flex items-center gap-3 text-base text-gray-700">
                  <FiPackage className="text-xl text-gray-800" />
                  <span>
                    등록한 팩 수량{" "}
                    <strong className="text-blue-500">
                      {registeredPackCountByDate.get(date) ?? 0}개
                    </strong>
                  </span>
                </div>
              </div>
              <div className="px-4 overflow-scroll">
                {/* 해당 날짜에 속하는 제품들 렌더링 */}
                {groupedProducts[date]
                  ?.filter(
                    (product) =>
                      product.isDeleted === false || product.isDeleted === null
                  )
                  .map((product) => (
                    <div
                      key={product.id}
                      className={`flex overflow-scroll flex-col mt-6 ${
                        product.order?.isPickupCompleted
                          ? "bg-green-100"
                          : "bg-white"
                      }`}
                    >
                      {/* 판매가 이루어진 팩만 문의가 가능 */}
                      {product.order?.id && (
                        <div className="flex justify-between items-center">
                          <div className="text-2xl font-bold text-gray-600">
                            {product.order?.user?.name || "익명의 히어로"}
                          </div>
                          <div>
                            <button
                              onClick={() => {
                                setReportModalOpen(true);
                                setSelectedReportOrderId(
                                  product.order?.id ?? null
                                );
                                setReportReason("");
                              }}
                              className="flex items-center gap-2 bg-white border-red-500 border text-red-500 px-3 py-1 rounded"
                            >
                              <HiExclamationTriangle className="text-red-500" />{" "}
                              문의
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="flex overflow-x-scroll">
                        {(product.menus?.length ?? 0) > 0 ? (
                          <div className="flex gap-1">
                            {[...(product.menus ?? [])]
                              .reverse()
                              .map((menu) => {
                                return (
                                  <div
                                    key={menu.id}
                                    className="relative w-32 h-32"
                                  >
                                    <ResponsiveCDNImage
                                      src={menu.img}
                                      alt=""
                                      className="w-32 h-32 rounded-md object-cover"
                                      sizeRatio={1 / 2}
                                    />
                                    <div className="absolute bottom-1 right-1.5 rounded-md opacity-80 bg-black text-white p-0.5 px-1.5">
                                      <p className="text-md font-semibold">
                                        x{menu.quantity}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div className="w-[137px] h-[137px]">
                            <ResponsiveCDNImage
                              src={product.img ?? ""}
                              alt="Menu"
                              className="w-[137px] h-[137px] object-cover rounded-lg"
                              sizeRatio={1 / 2}
                            />
                          </div>
                        )}
                      </div>

                      <div className="flex w-full flex-col">
                        {/* 제품 이미지 */}
                        <div className="flex">
                          <div className="text-xl break-keep font-semibold mt-2 max-w-48">
                            {product.name}
                          </div>
                        </div>
                        <div className="mt-1">
                          <div className="flex justify-between items-center">
                            <div className="flex">
                              <p className="text-gray-400 text-base">
                                정가: {product.price.toLocaleString()}
                              </p>
                              <p className="text-gray-400 text-base ml-1">
                                할인가:{" "}
                                {product.discountPrice?.toLocaleString()}
                              </p>
                            </div>
                            <div className="flex justify-end">
                              <p className="text-xl text-gray-900 font-bold">
                                {product?.userPrice
                                  ? Math.floor(
                                      product.userPrice
                                    ).toLocaleString()
                                  : Math.floor(
                                      ((product.discountPrice +
                                        product.discountPrice * 0.1) /
                                        10) *
                                        10
                                    ).toLocaleString()}
                                원<span className="text-gray-900"></span>
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="mt-2 flex items-end w-full h-full justify-between">
                        <div className="rounded bg-gray-100">
                          <div className="flex items-center px-3 py-2 text-center">
                            <span className="font-semibold text-blue-600">
                              판매완료
                            </span>
                            <span className="mx-3 h-4 w-px bg-gray-300" />
                            <strong className="text-gray-900">
                              {product.soldQuantity}개
                            </strong>
                          </div>
                          {product.order?.user && product.order?.isApproved && (
                            <>
                              {product.order?.identificationCode && (
                                <p className="text-md px-2 pb-1">
                                  손님 확인 코드:{" "}
                                  <span className="font-black tracking-wider underline underline-offset-2">
                                    {product.order.identificationCode}
                                  </span>
                                </p>
                              )}
                              <p className="text-md px-2 pb-1">
                                구매자:{" "}
                                <span className="text-black font-bold">
                                  {product.order.user.name || "익명의 히어로"}
                                </span>
                              </p>
                              <p className="text-md px-2 pb-1">
                                픽업시간:{" "}
                                <span className="text-black font-bold">
                                  {product.order?.pickUpTime}
                                </span>
                                <div className="mt-1 text-black font-semibold">
                                  {product.order?.user?.phone?.replace(
                                    /(\d{3})(\d{4})(\d{4})/,
                                    "$1-$2-$3"
                                  )}
                                </div>
                              </p>
                            </>
                          )}
                          {product.order &&
                            !product.order?.isCanceled &&
                            product.order?.isApproved && (
                              <div className="flex px-2 py-2 bg-gray-100 border">
                                <div className="flex items-center gap-2">
                                  손님이 픽업을 완료했나요?
                                  <button
                                    className={`flex items-center justify-center w-12 h-6 rounded-full transition-colors duration-300 ${
                                      product.order?.isPickupCompleted
                                        ? "bg-green-500"
                                        : "bg-gray-300"
                                    }`}
                                    onClick={() => {
                                      if (product.order?.id) {
                                        handleCompletePickup(product.order.id);
                                      }
                                    }}
                                  >
                                    <span
                                      className={`w-6 h-6 bg-white rounded-full transform transition-transform duration-300 ${
                                        product.order?.isPickupCompleted
                                          ? "translate-x-[12px]"
                                          : "translate-x-[-12px]"
                                      }`}
                                    ></span>
                                  </button>
                                </div>
                              </div>
                            )}
                        </div>
                        {/* 버튼 */}
                        {!product.order?.isCanceled &&
                          product.order &&
                          product.order?.isApproved && (
                            <div className="flex gap-2">
                              <button
                                className="px-2 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                onClick={() => handleCancel(product)}
                              >
                                결제 취소
                              </button>
                            </div>
                          )}
                        {!product.order?.isCanceled &&
                          !product.order?.isApproved &&
                          product.isToday && (
                            <div className="flex gap-2">
                              <button
                                className="px-2 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                onClick={() => handleEdit(product.id)}
                              >
                                수정
                              </button>
                              <button
                                className={clsx(
                                  "px-2 py-2 bg-red-500 text-white rounded hover:bg-red-600 min-w-12 h-10",
                                  loadingProductId === product.id &&
                                    "pointer-events-none opacity-80"
                                )}
                                onClick={() => handleDelete(product.id)}
                                disabled={loadingProductId === product.id}
                              >
                                {loadingProductId === product.id ? (
                                  <LoadingSpinnerInline />
                                ) : (
                                  "삭제"
                                )}
                              </button>
                            </div>
                          )}
                        {product.order?.isCanceled && (
                          <div className="flex gap-2">
                            <div
                              className="px-2 py-2 text-red-500 font-bold rounded hover:bg-blue-600 text-lg"
                              onClick={() => handleCancel(product)}
                            >
                              취소 완료
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="w-full bg-gray-200 h-[1px] mt-4" />
                    </div>
                  ))}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center h-[300px] mt-20">
            <div className="flex justify-center items-center h-48">
              {/* <img
                src={IMG1}
                alt="실시간판매"
                className="max-h-56 bg-gray-200 rounded-2xl"
              /> */}
            </div>
            <p className="text-gray-500 text-xl font-bold text-center mt-8">
              아직 판매 기록이 없네요 📦
            </p>
          </div>
        )}

        {/* 더 불러오기 */}
        {hasMore && (
          <div className="flex justify-center py-6 px-4">
            <button
              type="button"
              onClick={handleLoadMore}
              disabled={loading || isFetchingMore}
              className={clsx(
                "w-full max-w-md py-4 rounded-xl font-bold text-center transition-colors flex items-center justify-center",
                "bg-blue-500 text-white hover:bg-blue-600",
                (loading || isFetchingMore) && "opacity-70 pointer-events-none"
              )}
            >
              {isFetchingMore ? <LoadingSpinnerInline /> : "더 불러오기"}
            </button>
          </div>
        )}

        {isModalOpen && (
          <CancelModal
            title="취소환불 처리"
            message="정말로 취소하시겠어요? 사유를 적어주세요!"
            onConfirm={() => {
              onConfirm(); // 모달 닫기
              setIsModalOpen(false);
            }}
            onCancel={() => setIsModalOpen(false)} // 모달 취소
            reason={reason}
            handleReason={handleReason}
          />
        )}

        {isReportModalOpen && selectedReportOrderId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-8 min-w-[340px] max-w-[90vw] rounded shadow-lg">
              <div className="text-2xl font-bold mb-6 text-center">
                이 손님이 맞나요?
              </div>
              <div className="bg-gray-100 rounded-lg p-4 mb-4">
                <div className="text-xl text-gray-600 mb-1">
                  이름:{" "}
                  <span className="font-bold text-blue-700">
                    {data?.productsBySeller?.find(
                      (product) =>
                        product.order?.id === selectedReportOrderId
                    )?.order?.user?.name || "익명의 히어로"}
                  </span>
                </div>
                <div className="text-xl text-gray-600">
                  전화번호:{" "}
                  <span className="font-bold text-blue-700">
                    {data?.productsBySeller?.find(
                      (product) =>
                        product.order?.id === selectedReportOrderId
                    )?.order?.user?.phone || "없음"}
                  </span>
                </div>
              </div>
              <div className="text-xl text-gray-800 font-semibold mb-6">
                어떤 상황인지 알려주세요.
                <br />
                빠르게 검토하고 연락드릴게요!
              </div>
              <div className="text-gray-600 mb-4">
                <span className="font-bold">예시:</span>
                <ul>
                  <li>· 손님이 오지 않았나요?</li>
                  <li>· 손님이 무리한 요구를 했나요?</li>
                  <li>· 손님이 픽업시간보다 늦게 왔나요?</li>
                </ul>
              </div>
              <textarea
                className="w-full p-3 border border-gray-300 rounded mb-4 text-lg"
                value={reportReason}
                rows={5}
                onChange={(event) => setReportReason(event.target.value)}
                placeholder="상황을 입력해 주세요"
              />
              <div className="flex justify-center gap-4 mt-4">
                <button
                  className="bg-gray-500 text-white px-5 py-2 rounded"
                  onClick={() => {
                    setReportModalOpen(false);
                    setReportReason("");
                  }}
                >
                  취소
                </button>
                <button
                  className="bg-blue-500 text-white px-5 py-2 rounded"
                  onClick={() => {
                    if (window.confirm("문의를 제출할까요?")) {
                      handleReportOrder(selectedReportOrderId, reportReason);
                      setReportModalOpen(false);
                      setReportReason("");
                    }
                  }}
                  disabled={reportLoading || !reportReason.trim()}
                >
                  {reportLoading ? <LoadingSpinnerInline /> : "완료"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveOrderPreviousSales;
